/**
 * cancel_all_runs tool — cancel every in-flight run of a flow in one call.
 *
 * Composes the existing runs listing and per-run cancel: there is no batch
 * cancel endpoint, so this filters to cancellable statuses and cancels each,
 * reporting per-run outcomes rather than failing the whole call on one error.
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import type { FlowRun } from "../api/types.js";
import { logger } from "../utils/logger.js";
import { ConfirmationRequiredError } from "../utils/errors.js";

/** Statuses the service accepts a cancel for; everything else is already over. */
const CANCELLABLE_STATUSES = new Set(["Running", "Waiting", "Paused", "Suspended"]);

export const cancelAllRunsInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID) whose runs should be cancelled"),
  environment: z.string().optional().describe("Environment name or GUID. Uses default if omitted."),
  confirm: z
    .boolean()
    .optional()
    .default(false)
    .describe("Set true to actually cancel; false previews what would be cancelled"),
  top: z
    .number()
    .int()
    .min(1)
    .max(250)
    .optional()
    .describe("How many recent runs to examine (default 50)"),
});

export const cancelAllRunsTool = {
  name: "cancel_all_runs",
  description:
    "Cancel every in-flight run of a flow (Running, Waiting, Paused, Suspended). Without confirm=true it previews which runs would be cancelled. Cancelled runs cannot be resumed.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: { type: "string", description: "The flow ID (GUID)" },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
      confirm: {
        type: "boolean",
        description: "Set true to cancel; omit to preview the affected runs first",
      },
      top: {
        type: "number",
        minimum: 1,
        maximum: 250,
        description: "How many recent runs to examine (default 50)",
      },
    },
    required: ["flowId"],
  },
};

function describeRun(run: FlowRun): string {
  return `${run.name} (${run.properties.status}, started ${run.properties.startTime})`;
}

export async function handleCancelAllRuns(
  api: FlowManagementApi,
  input: unknown,
): Promise<string> {
  const parsed = cancelAllRunsInputSchema.parse(input);
  logger.info({ flowId: parsed.flowId, confirm: parsed.confirm }, "Executing cancel_all_runs");

  const runs = await api.getFlowRuns(parsed.flowId, parsed.environment, parsed.top ?? 50);
  const active = runs.filter((r) => CANCELLABLE_STATUSES.has(r.properties.status));

  if (active.length === 0) {
    return `No in-flight runs to cancel for flow ${parsed.flowId} (examined ${runs.length} recent run${runs.length === 1 ? "" : "s"}).`;
  }

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError([
      `${active.length} in-flight run${active.length === 1 ? "" : "s"} would be cancelled:`,
      ...active.map((r) => `  ${describeRun(r)}`),
      "",
      "Re-run with confirm=true to cancel them. Cancelled runs cannot be resumed.",
    ].join("\n"));
  }

  // One failure must not strand the rest — cancel each, collect outcomes.
  const results = await Promise.allSettled(
    active.map((run) => api.cancelRun(parsed.flowId, run.name, parsed.environment)),
  );
  const cancelled: string[] = [];
  const failed: string[] = [];
  results.forEach((result, index) => {
    const run = active[index];
    if (!run) return;
    if (result.status === "fulfilled") {
      cancelled.push(run.name);
    } else {
      const reason = result.reason instanceof Error ? result.reason.message : String(result.reason);
      failed.push(`${run.name}: ${reason}`);
    }
  });

  const lines = [`Cancelled ${cancelled.length} of ${active.length} in-flight run(s).`];
  if (cancelled.length > 0) lines.push(...cancelled.map((id) => `  cancelled: ${id}`));
  if (failed.length > 0) {
    lines.push(`${failed.length} could not be cancelled (they may have completed first):`);
    lines.push(...failed.map((f) => `  ${f}`));
  }
  return lines.join("\n");
}
